import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileAlt, FaDownload, FaSearch, FaEye } from 'react-icons/fa';

const CertificatesPage = () => {
  const { t } = useTranslation();
  const certificates = [
    {
      id: 1,
      name: t('certificates.items.iso9001.name'),
      description: t('certificates.items.iso9001.description'),
      issueDate: '2022-05-15',
      validUntil: '2025-05-14',
      image: '/certificates/iso9001.jpg'
    },
    {
      id: 2,
      name: t('certificates.items.iso14001.name'),
      description: t('certificates.items.iso14001.description'),
      issueDate: '2022-06-20',
      validUntil: '2025-06-19',
      image: '/certificates/iso14001.jpg'
    },
    {
      id: 3,
      name: t('certificates.items.ohsas18001.name'),
      description: t('certificates.items.ohsas18001.description'),
      issueDate: '2021-11-10',
      validUntil: '2024-11-09',
      image: '/certificates/ohsas18001.jpg'
    },
    {
      id: 4,
      name: t('certificates.items.ce.name'),
      description: t('certificates.items.ce.description'),
      issueDate: '2023-02-05',
      validUntil: '2028-02-04',
      image: '/certificates/ce.jpg'
    },
    {
      id: 5,
      name: t('certificates.items.tse.name'),
      description: t('certificates.items.tse.description'),
      issueDate: '2023-04-18',
      validUntil: '2026-04-17',
      image: '/certificates/tse.jpg'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('certificates.hero.title')}</h1>
          <p className="text-xl max-w-3xl">
            {t('certificates.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Certificates Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <h2 className="text-3xl font-bold mb-6">{t('certificates.section.title')}</h2>
            <p className="text-gray-300">
              {t('certificates.section.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificates.map((certificate) => (
              <div key={certificate.id} className="bg-dark-light rounded-lg shadow-md overflow-hidden">
                <div className="h-64 bg-gray-200 relative">
                  <img
                    src={certificate.image}
                    alt={certificate.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://via.placeholder.com/600x400?text=${certificate.name.replace(':', '').replace(' ', '+')}`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <div className="flex space-x-4">
                      <button className="bg-white p-3 rounded-full hover:bg-gray-100 transition-colors">
                        <FaEye className="text-gray-800" />
                      </button>
                      <button className="bg-primary p-3 rounded-full hover:bg-blue-700 transition-colors">
                        <FaDownload className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start mb-4">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaFileAlt className="text-primary text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1 text-white">{certificate.name}</h3>
                      <p className="text-gray-300 text-sm">{certificate.description}</p>
                    </div>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-gray-400">Issue Date</p>
                        <p className="font-medium text-white">{new Date(certificate.issueDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Valid Until</p>
                        <p className="font-medium text-white">{new Date(certificate.validUntil).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Policy */}
      <section className="py-16 bg-dark-medium">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">{t('certificates.policy.title')}</h2>
            <div className="bg-dark-light p-8 rounded-lg shadow-md">
              <div className="space-y-6">
                <p className="text-gray-300">
                  {t('certificates.policy.intro')}
                </p>
                <ul className="space-y-4">
                  <li className="flex">
                    <span className="bg-primary/10 p-2 rounded-full mr-4 h-min mt-1">
                      <FaCheck className="text-primary" />
                    </span>
                    <div>
                      <strong className="text-white">{t('certificates.policy.points.excellence.title')}</strong>
                      <p className="text-gray-300">{t('certificates.policy.points.excellence.description')}</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-primary/10 p-2 rounded-full mr-4 h-min mt-1">
                      <FaCheck className="text-primary" />
                    </span>
                    <div>
                      <strong className="text-white">{t('certificates.policy.points.improvement.title')}</strong>
                      <p className="text-gray-300">{t('certificates.policy.points.improvement.description')}</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-primary/10 p-2 rounded-full mr-4 h-min mt-1">
                      <FaCheck className="text-primary" />
                    </span>
                    <div>
                      <strong className="text-white">{t('certificates.policy.points.environmental.title')}</strong>
                      <p className="text-gray-300">{t('certificates.policy.points.environmental.description')}</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-primary/10 p-2 rounded-full mr-4 h-min mt-1">
                      <FaCheck className="text-primary" />
                    </span>
                    <div>
                      <strong className="text-white">{t('certificates.policy.points.safety.title')}</strong>
                      <p className="text-gray-300">{t('certificates.policy.points.safety.description')}</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-primary/10 p-2 rounded-full mr-4 h-min mt-1">
                      <FaCheck className="text-primary" />
                    </span>
                    <div>
                      <strong className="text-white">{t('certificates.policy.points.compliance.title')}</strong>
                      <p className="text-gray-300">{t('certificates.policy.points.compliance.description')}</p>
                    </div>
                  </li>
                </ul>
                <p className="text-gray-300">
                  {t('certificates.policy.conclusion')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const FaCheck = ({ className }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className={className} 
      width="16" 
      height="16" 
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
};

export default CertificatesPage;
